import {inject, injectable} from 'inversify';

import type {
  CoreClientMembershipsCancelParams,
  CoreClientMembershipsRestartParams,
} from '../../../../../api/types/core/methods';
import type {CourseModel} from '../../../../domain/Course/Course.model';
import type {MembershipModel} from '../../../../domain/Membership/Membership.model';
import type {ToggleEmployeeSubscriptionCmd} from '../ToggleEmployeeSubscription.cmd';

import {apiRequest} from '../../../../../api/utils/apiRequest';
import {AppError} from '../../../../../utils/errors/app';
import {CourseMapperJrpc} from '../../../../domain/Course/mappers/Course.mapper.jrpc';
import {MembershipMapperJrpc} from '../../../../domain/Membership/mappers/Membership.mapper.jrpc';

@injectable()
export class ToggleEmployeeSubscriptionCmdJrpc
  implements ToggleEmployeeSubscriptionCmd
{
  constructor(
    @inject(MembershipMapperJrpc)
    private readonly membershipMapper: MembershipMapperJrpc,
    @inject(CourseMapperJrpc) private readonly courseMapper: CourseMapperJrpc,
  ) {}

  async exec(
    props: ToggleEmployeeSubscriptionCmd.Props,
  ): Promise<ToggleEmployeeSubscriptionCmd.Response> {
    const request = this.requestFactory(props);

    if (!request) {
      throw new AppError('Invalid props. Fail build request');
    }

    const response = await request;

    return {
      course: this.getCourseData(
        this.courseMapper.mapDataToProps(response.course),
      ),
      membership: this.getMembershipData(
        this.membershipMapper.mapDataToProps(response.membership),
      ),
    };
  }

  private getCourseData({
    id,
    isAutoRestarting,
    restart,
    updatedDate,
  }: CourseModel.Props) {
    return {
      id,
      isAutoRestarting,
      restart,
      updatedDate,
    };
  }

  private getMembershipData({cardId, id}: MembershipModel.Props) {
    return {cardId, id};
  }

  private async requestFactory({
    cancel,
    restart,
  }: ToggleEmployeeSubscriptionCmd.Props) {
    if (cancel) {
      return apiRequest<CoreClientMembershipsCancelParams>({
        method: 'client.memberships.cancel',
        params: {
          _id: cancel.id,
        },
      });
    }

    if (restart) {
      return apiRequest<CoreClientMembershipsRestartParams>({
        method: 'client.memberships.restart',
        params: {
          _id: restart.id,
          card_id: restart.cardId,
        },
      });
    }

    return null;
  }
}
